import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY_ENTITY_FORM = {
  name: '',
  description: '',
  entity_type: 'industry_partners',
  business_group: '',
  image_url: '',
  image_gallery: '',
  location: '',
  latitude: null,
  longitude: null,
  tags: '',
  contact_email: '',
  website: '',
  promo_codes: '',
  offers: '',
  specialties: '',
  years_experience: 0,
  certifications: '',
};

/**
 * Entity list management for the Admin Panel — list/filters/pagination,
 * row selection, the create/edit form, bulk select, CRUD, verify, export.
 *
 * Cross-hook wiring: pass `refetchStats` so save/delete actions can refresh
 * the dashboard counters in lockstep with the entities list.
 */
export function useAdminEntities({ token, activeTab, isAdmin, user, refetchStats }) {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const [pagination, setPagination] = useState({ skip: 0, limit: 50, total: 0 });

  // Modal + form
  const [showEntityModal, setShowEntityModal] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);
  const [entityForm, setEntityForm] = useState(EMPTY_ENTITY_FORM);
  const [galleryImages, setGalleryImages] = useState([]);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);

  // Bulk select
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip: pagination.skip,
        limit: pagination.limit,
        search: searchQuery || undefined,
        entity_type: filterType !== 'all' ? filterType : undefined,
        verified: filterVerified !== 'all' ? filterVerified === 'verified' : undefined,
      };
      const response = await axios.get(`${API}/admin/entities`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setEntities(response.data.entities || []);
      setPagination((prev) => ({ ...prev, total: response.data.total }));
    } catch (error) {
      console.error('Error fetching entities:', error);
    } finally {
      setLoading(false);
    }
  }, [token, pagination.skip, pagination.limit, searchQuery, filterType, filterVerified]);

  useEffect(() => {
    if (activeTab === 'entities' && isAdmin) {
      fetchEntities();
    }
    // Clear bulk selection whenever the entity list query changes
    setSelectedEntityIds([]);
  }, [activeTab, fetchEntities, isAdmin]);

  // ---------- File upload (profile + gallery) ----------
  const handleFileUpload = async (file, type = 'profile') => {
    if (!file) return null;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const fileUrl = process.env.REACT_APP_BACKEND_URL + response.data.url;
      if (type === 'profile') {
        setEntityForm((f) => ({ ...f, image_url: fileUrl }));
        setProfileImagePreview(fileUrl);
      } else if (type === 'gallery') {
        if (galleryImages.length >= 10) {
          toast.error('Maximum 10 gallery images allowed');
          return null;
        }
        setGalleryImages((imgs) => [...imgs, fileUrl]);
      }
      toast.success('File uploaded successfully');
      return fileUrl;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveGalleryImage = (index) => {
    setGalleryImages((imgs) => imgs.filter((_, i) => i !== index));
  };

  /** Replace the gallery list with a reordered copy (used by drag-and-drop). */
  const handleReorderGalleryImages = (newOrder) => {
    setGalleryImages(newOrder);
  };

  // Upload multiple gallery files in parallel, capped at 10 total images.
  // Returns { uploaded, skipped, failed } counts so the UI can report
  // exactly what happened (some files may be too large, wrong type, etc.).
  const handleGalleryUploadMultiple = async (fileList) => {
    if (!fileList || fileList.length === 0) return { uploaded: 0, skipped: 0, failed: 0 };

    const remainingSlots = 10 - galleryImages.length;
    if (remainingSlots <= 0) {
      toast.error('Gallery is full (10 images max). Remove one to add more.');
      return { uploaded: 0, skipped: fileList.length, failed: 0 };
    }

    const incoming = Array.from(fileList);
    const accepted = incoming.slice(0, remainingSlots);
    const skipped = incoming.length - accepted.length;

    setUploadingFile(true);
    const toastId = toast.loading(`Uploading ${accepted.length} image${accepted.length === 1 ? '' : 's'}…`);

    try {
      const settled = await Promise.allSettled(
        accepted.map((file) => {
          const formData = new FormData();
          formData.append('file', file);
          return axios.post(`${API}/upload`, formData, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          });
        })
      );

      const newUrls = [];
      let failed = 0;
      settled.forEach((res, idx) => {
        if (res.status === 'fulfilled') {
          newUrls.push(process.env.REACT_APP_BACKEND_URL + res.value.data.url);
        } else {
          failed += 1;
          console.error('Gallery upload failed for', accepted[idx]?.name, res.reason);
        }
      });

      if (newUrls.length > 0) {
        setGalleryImages((imgs) => [...imgs, ...newUrls]);
      }

      const uploaded = newUrls.length;
      if (uploaded > 0 && failed === 0 && skipped === 0) {
        toast.success(`${uploaded} image${uploaded === 1 ? '' : 's'} uploaded`, { id: toastId });
      } else if (uploaded > 0) {
        const parts = [`${uploaded} uploaded`];
        if (failed > 0) parts.push(`${failed} failed`);
        if (skipped > 0) parts.push(`${skipped} skipped (gallery cap)`);
        toast.warning(parts.join(' · '), { id: toastId });
      } else {
        toast.error('No images uploaded — see console for details', { id: toastId });
      }
      return { uploaded, skipped, failed };
    } catch (error) {
      console.error('Gallery batch upload error:', error);
      toast.error('Gallery upload failed', { id: toastId });
      return { uploaded: 0, skipped: 0, failed: accepted.length };
    } finally {
      setUploadingFile(false);
    }
  };

  // ---------- Form lifecycle ----------
  const resetEntityForm = () => {
    setEntityForm(EMPTY_ENTITY_FORM);
    setGalleryImages([]);
    setProfileImagePreview('');
    setEditingEntity(null);
  };

  const openEditModal = (entity) => {
    setEditingEntity(entity);
    setEntityForm({
      name: entity.name || '',
      description: entity.description || '',
      entity_type: entity.entity_type || 'industry_partners',
      business_group: entity.business_group || '',
      image_url: entity.image_url || '',
      image_gallery: (entity.image_gallery || []).join(', '),
      location: entity.location || '',
      latitude: entity.latitude ?? null,
      longitude: entity.longitude ?? null,
      tags: (entity.tags || []).join(', '),
      contact_email: entity.contact_email || '',
      website: entity.website || '',
      promo_codes: entity.promo_codes?.length > 0 ? JSON.stringify(entity.promo_codes, null, 2) : '',
      offers: entity.offers?.length > 0 ? JSON.stringify(entity.offers, null, 2) : '',
      specialties: (entity.specialties || []).join(', '),
      years_experience: entity.years_experience || 0,
      certifications: (entity.certifications || []).join(', '),
    });
    setGalleryImages(entity.image_gallery || []);
    setProfileImagePreview(entity.image_url || '');
    setShowEntityModal(true);
  };

  // ---------- CRUD ----------
  const handleCreateEntity = async () => {
    try {
      const payload = {
        ...entityForm,
        // Normalise the business_group free-text so identical values (with
        // stray whitespace) group together on the admin table and public
        // cards. Empty string becomes null so we don't create ghost groups.
        business_group: (entityForm.business_group || '').trim() || null,
        tags: entityForm.tags.split(',').map((t) => t.trim()).filter(Boolean),
        image_gallery: galleryImages,
        specialties: entityForm.specialties.split(',').map((t) => t.trim()).filter(Boolean),
        certifications: entityForm.certifications.split(',').map((t) => t.trim()).filter(Boolean),
        promo_codes: entityForm.promo_codes ? JSON.parse(entityForm.promo_codes) : [],
        offers: entityForm.offers ? JSON.parse(entityForm.offers) : [],
      };

      if (editingEntity) {
        await axios.put(`${API}/admin/entities/${editingEntity.entity_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Partner updated successfully');
      } else {
        await axios.post(`${API}/admin/entities`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Partner created successfully');
      }

      setShowEntityModal(false);
      resetEntityForm();
      fetchEntities();
      refetchStats?.();
    } catch (error) {
      console.error('Error saving entity:', error);
      toast.error(error.response?.data?.detail || 'Failed to save partner');
    }
  };

  const handleDeleteEntity = async (entityId) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    try {
      await axios.delete(`${API}/admin/entities/${entityId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Partner deleted');
      fetchEntities();
      refetchStats?.();
    } catch (error) {
      console.error('Error deleting entity:', error);
      toast.error('Failed to delete partner');
    }
  };

  const handleVerifyEntity = async (entityId, verified) => {
    try {
      await axios.post(
        `${API}/admin/entities/${entityId}/verify?verified=${verified}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Partner ${verified ? 'verified' : 'unverified'}`);
      fetchEntities();
      refetchStats?.();
    } catch (error) {
      console.error('Error verifying entity:', error);
      toast.error('Failed to update verification status');
    }
  };

  const handleToggleFeatured = async (entity) => {
    const next = !entity.is_featured;
    try {
      await axios.put(
        `${API}/admin/entities/${entity.entity_id}/featured?featured=${next}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success(next ? 'Marked as featured' : 'Removed from featured');
      fetchEntities();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Could not update featured: ${detail}`);
    }
  };

  const handleBulkSetFeatured = async (featured) => {
    if (selectedEntityIds.length === 0) return;
    try {
      const res = await axios.post(
        `${API}/admin/entities/bulk-feature`,
        { entity_ids: selectedEntityIds, featured },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      const verb = featured ? 'featured' : 'unfeatured';
      const n = res.data?.modified_count ?? 0;
      toast.success(`${n} partner${n === 1 ? '' : 's'} ${verb}`);
      setSelectedEntityIds([]);
      fetchEntities();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Bulk feature failed: ${detail}`);
    }
  };

  // ---------- Bulk select + delete ----------
  const toggleEntitySelection = (entityId) => {
    setSelectedEntityIds((prev) =>
      prev.includes(entityId) ? prev.filter((id) => id !== entityId) : [...prev, entityId]
    );
  };

  const toggleSelectAllEntities = (checked) => {
    if (checked) {
      setSelectedEntityIds(entities.map((e) => e.entity_id));
    } else {
      setSelectedEntityIds([]);
    }
  };

  // Ask the backend to nudge WordPress mshots for every entity without an
  // uploaded image. The endpoint returns instantly with a "scheduled" count;
  // the actual crawls happen in the background and land in the Audit log.
  const handlePrewarmScreenshots = async () => {
    try {
      const res = await axios.post(
        `${API}/admin/entities/prewarm-screenshots`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(res.data?.message || 'Prewarm scheduled');
    } catch (err) {
      console.error('Prewarm error:', err);
      toast.error(err.response?.data?.detail || 'Could not schedule prewarm');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedEntityIds.length === 0) return;
    const isModerator = user?.admin_role === 'moderator';
    const verb = isModerator ? 'suspend' : 'permanently delete';
    if (!window.confirm(
      `Are you sure you want to ${verb} ${selectedEntityIds.length} ${selectedEntityIds.length === 1 ? 'partner' : 'partners'}? This action cannot be undone.`
    )) return;

    setBulkDeleting(true);
    try {
      const response = await axios.post(
        `${API}/admin/entities/bulk-delete`,
        { entity_ids: selectedEntityIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const count = response.data.deleted_count ?? response.data.modified_count ?? selectedEntityIds.length;
      toast.success(`${count} ${count === 1 ? 'entity' : 'entities'} ${isModerator ? 'suspended' : 'deleted'}`);
      setSelectedEntityIds([]);
      fetchEntities();
      refetchStats?.();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  // ---------- Export ----------
  const handleExportEntities = async (format) => {
    try {
      const params = new URLSearchParams({ format });
      if (filterType && filterType !== 'all') params.append('entity_type', filterType);
      if (filterVerified === 'verified') params.append('verified', 'true');
      else if (filterVerified === 'pending') params.append('verified', 'false');
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(`${API}/admin/entities/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: format === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `entities-${stamp}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      const count = response.headers?.['x-entity-count'];
      toast.success(`Exported ${count ?? ''} entit${count === '1' ? 'y' : 'ies'} as ${format.toUpperCase()}`.trim());
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export entities');
    }
  };

  return {
    // data
    entities,
    loading,
    pagination,
    setPagination,
    // filters
    searchQuery,
    setSearchQuery,
    filterType,
    setFilterType,
    filterVerified,
    setFilterVerified,
    // modal + form
    showEntityModal,
    setShowEntityModal,
    editingEntity,
    entityForm,
    setEntityForm,
    galleryImages,
    profileImagePreview,
    uploadingFile,
    // bulk select
    selectedEntityIds,
    setSelectedEntityIds,
    bulkDeleting,
    // handlers
    fetchEntities,
    resetEntityForm,
    openEditModal,
    handleFileUpload,
    handleGalleryUploadMultiple,
    handleRemoveGalleryImage,
    handleReorderGalleryImages,
    handleCreateEntity,
    handleDeleteEntity,
    handleVerifyEntity,
    handleToggleFeatured,
    handleBulkSetFeatured,
    toggleEntitySelection,
    toggleSelectAllEntities,
    handleBulkDelete,
    handlePrewarmScreenshots,
    handleExportEntities,
  };
}
